'use client'
import React from 'react'
import { motion } from 'framer-motion'
import { Gift, Crown, Check, Building2 } from 'lucide-react'
import { DISCOUNT_ENABLED, PLAN_PRICES } from '@/planHelper'
import { ForwardRefExoticComponent, RefAttributes } from 'react'
import { LucideProps } from 'lucide-react'
import { handleSignUpClick } from '@/lib/tracking'

type LucideIcon = ForwardRefExoticComponent<Omit<LucideProps, 'ref'> & RefAttributes<SVGSVGElement>>

interface Plan {
	name: string
	icon: LucideIcon
	price: string
	priceUnit?: string
	offerPrice?: string
	offerLabel?: string
	subtitle: string
	features: string[]
	iconColor: string
	iconBorderColor: string
	isEnterprise?: boolean
}

const PricingSection = () => {
	const plans: Plan[] = [
		{
			name: 'Free',
			icon: Gift,
			price: PLAN_PRICES.free.price,
			subtitle: 'Get started with core features',
			features: [
				'Unlimited PR reviews',
				'Unlimited public and private repositories',
				'Code repository configuration'
			],
			iconColor: '#10B981',
			iconBorderColor: '#10B981'
		},
		{
			name: 'Pro',
			icon: Crown,
			price: PLAN_PRICES.pro.price,
			priceUnit: PLAN_PRICES.pro.priceUnit,
			offerPrice: DISCOUNT_ENABLED ? PLAN_PRICES.pro.offerPrice : undefined,
			offerLabel: DISCOUNT_ENABLED ? PLAN_PRICES.pro.offerLabel : undefined,
			subtitle: 'Advanced features for growing teams',
			features: [
				'Everything in FREE',
				'3 day free trial',
				'Agentic Chat',
				'Integrations (Slack, Discord)',
				'Security Alerts',
				'Code Explorer',
				'Priority Support'
			],
			iconColor: '#8B5CF6',
			iconBorderColor: '#8B5CF6'
		},
		{
			name: 'Enterprise',
			icon: Building2,
			price: PLAN_PRICES.enterprise.price,
			subtitle: 'Tailored solutions for large organizations',
			features: [
				'Everything in PRO',
				'Custom AI Models',
				'Dedicated Support',
				'On-premise Deployment',
				'Custom Integrations'
			],
			iconColor: '#F59E0B',
			iconBorderColor: '#F59E0B',
			isEnterprise: true
		}
	]

	return (
		<section id="pricing" className="relative overflow-hidden bg-[#020F2A] py-16">
			<div className="absolute inset-0 z-0">
				<div className="absolute inset-0 bg-gradient-to-br from-transparent via-[#020F2A]/60 to-[#3BCF8E]/30" />
				<div
					className="absolute inset-0 bg-repeat opacity-10"
					style={{
						backgroundImage:
							'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 40 40\'><g fill=\'none\' fill-rule=\'evenodd\'><g fill=\'%23000000\'><path d=\'M0 38.59l2.83-2.83 1.41 1.41L1.41 40H0v-1.41zM0 1.4l2.83 2.83 1.41-1.41L1.41 0H0v1.41zM38.59 40l-2.83-2.83 1.41-1.41L40 38.59V40h-1.41zM40 1.41l-2.83 2.83-1.41-1.41L38.59 0H40v1.41zM20 18.6l2.83-2.83 1.41 1.41L21.41 20l2.83 2.83-1.41 1.41L20 21.41l-2.83 2.83-1.41-1.41L18.59 20l-2.83-2.83 1.41-1.41L20 18.59z\'/></g></g></svg>")',
						backgroundSize: '40px 40px'
					}}
				/>
			</div>
			<div className="relative z-10 max-w-7xl mx-auto px-4">
				<motion.div 
					className="text-center mb-12"
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					viewport={{ once: true }}
				>
					<h2 className="text-3xl md:text-6xl font-bold text-white mb-6">
						Choose Your
						<span className="block bg-gradient-to-r from-[#3BCF8E] to-[#2DA771] bg-clip-text text-transparent">
							Perfect Plan
						</span>
					</h2>
					<p className="text-xl text-gray-300 max-w-2xl mx-auto">
						Start free and scale as you grow. Upgrade anytime to unlock more powerful AI models and features.
					</p>
				</motion.div>

				<div className="grid grid-cols-1 md:grid-cols-3 items-end gap-2 mx-auto max-w-6xl">
					{plans.map((plan) => (
						<div
							key={plan.name}
							className={`relative bg-white rounded-2xl p-6 shadow-lg transition-all duration-300 border hover:border-[#3BCF8E]/30 flex flex-col ${
								plan.name === 'Pro' ? 'border-4 border-[#3BCF8E]' : 'border border-gray-200'
							}`}
						>
							{plan.name === 'Pro' && (
								<div className="absolute right-5 -top-[18px] rounded-xl bg-[#3BCF8E] px-4 py-1.5 text-sm font-semibold uppercase tracking-widest text-white">
									Popular
								</div>
							)}
							<div className="text-center mb-4">
								<div className="flex justify-center mb-3">
									<div 
										className="p-3 rounded-xl border"
										style={{
											backgroundColor:
												plan.name === 'Free'
													? '#F0FDF4'
													: plan.name === 'Lite'
													? '#EFF6FF'
													: plan.name === 'Pro'
													? '#F5F3FF'
													: '#FFFBEB',
											borderColor: plan.iconBorderColor
										}}
									>
										<plan.icon 
											className="h-8 w-8" 
											style={{ color: plan.iconColor }}
										/>
									</div>
								</div>
								
								<h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>

								<div className="mb-3">
									{plan.offerPrice ? (
										<>
											<p className="text-sm text-gray-400 line-through mb-1">{plan.price} {plan.priceUnit}</p>
											<div className="flex items-baseline justify-center gap-1.5">
												<span className="text-4xl font-bold text-gray-900">{plan.offerPrice}</span>
												<span className="text-gray-600">{plan.priceUnit}</span>
											</div>
											<p className="text-xs font-semibold text-[#2DA771] mt-1">{plan.offerLabel}</p>
										</>
									) : (
										<>
											<span className="text-4xl font-bold text-gray-900">{plan.price}</span>
											{plan.priceUnit && (
												<span className="text-gray-600 ml-1">{plan.priceUnit}</span>
											)}
										</>
									)}
								</div>

								<p className="text-gray-600 mb-3">{plan.subtitle}</p>
							</div>

							<div className="space-y-3 mb-6">
								{plan.features.map((feature, featureIndex) => (
									<div key={featureIndex} className="flex items-center">
										<Check className="h-5 w-5 text-[#3BCF8E] mr-3 flex-shrink-0" />
										<span className="text-gray-700">{feature}</span>
									</div>
								))}
							</div>

							<div className="border-t pt-4 flex flex-col">
								{plan.isEnterprise ? (
									<a
										href="https://meet.brevo.com/gentian-elmazi/infinitcode-ai-meeting"
										target="_blank"
										rel="noopener noreferrer"
										onClick={handleSignUpClick}
										className="mt-auto w-full bg-gradient-to-r from-[#3BCF8E] to-[#2DA771] text-white px-4 py-2 rounded-lg font-semibold text-sm text-center block hover:shadow-lg transition-all duration-300"
									>
										Contact us
									</a>
								) : (
									plan.name === 'Free' ? (
										<a
											href="https://app.infinitcode.ai/login"
											target="_blank"
											rel="noopener noreferrer"
											onClick={handleSignUpClick}
											className="mt-auto w-full bg-gradient-to-r from-[#3BCF8E] to-[#2DA771] text-white px-4 py-2 rounded-lg font-semibold text-sm text-center block hover:shadow-lg transition-all duration-300"
										>
											Sign up for free
										</a>
									) : (
										<a
											href={`https://app.infinitcode.ai/dashboard/billing?plan=${plan.name.toUpperCase()}`}
											target="_blank"
											rel="noopener noreferrer"
											onClick={handleSignUpClick}
											className="mt-auto w-full bg-gradient-to-r from-[#3BCF8E] to-[#2DA771] text-white px-4 py-2 rounded-lg font-semibold text-sm text-center block hover:shadow-lg transition-all duration-300"
										>
											Get a free trial
										</a>
									)
								)}
							</div>
						</div>
					))}
				</div>

				{/* Special Discounts Section */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, delay: 0.3 }}
					viewport={{ once: true }}
					className="mt-16"
				>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
						<motion.div
							whileHover={{ scale: 1.02 }}
							className="bg-transparent rounded-xl p-6 shadow-lg border-2 border-dashed border-[#3BCF8E]/60 hover:border-[#3BCF8E] transition-all duration-300"
						>
							<div className="text-center">
								<h4 className="text-lg font-semibold text-[#3BCF8E]">Always free for Open Source</h4>
							</div>
						</motion.div>
						
						<motion.div
							whileHover={{ scale: 1.02 }}
							className="bg-transparent rounded-xl p-6 shadow-lg border-2 border-dashed border-[#3BCF8E]/60 hover:border-[#3BCF8E] transition-all duration-300"
						>
							<div className="text-center">
								<h4 className="text-lg font-semibold text-[#3BCF8E]">Always free for startups up to 3 people</h4>
							</div>
						</motion.div>
					</div>
				</motion.div>
			</div>
		</section>
	)
}

export default PricingSection
